import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HOME_PATH } from '@/lib/paths';

const HISTORY_KEY = 'therappi_nav_history';
const MAX_HISTORY = 10;

export const useNavigationHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const history = getHistory();
    const currentPath = location.pathname;
    
    // Don't add duplicate consecutive entries
    if (history[history.length - 1] !== currentPath) {
      history.push(currentPath);
      
      // Keep only last MAX_HISTORY entries
      if (history.length > MAX_HISTORY) {
        history.shift();
      }
      
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    }
  }, [location.pathname]);

  const getHistory = (): string[] => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const getPreviousPage = (): string | null => {
    const history = getHistory();
    // Get the page before the current one
    return history.length >= 2 ? history[history.length - 2] : null;
  };

  const goBack = () => {
    const previousPage = getPreviousPage();
    
    if (previousPage) {
      // Remove current page from history before navigating
      const history = getHistory();
      history.pop();
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      navigate(previousPage);
    } else {
      // Default to home if no history
      navigate(HOME_PATH);
    }
  };

  const getBackButtonLabel = (): string => {
    const previousPage = getPreviousPage();
    
    if (!previousPage || previousPage === '/dashboard' || previousPage === '/') {
      return 'Home';
    }
    
    // Map routes to friendly names
    const routeNames: Record<string, string> = {
      '/manifestation': 'Manifestation',
      '/future-self-navigator': 'Future Self Navigator',
      '/therapy': 'Therapy',
      '/connect-and-grow': 'Connect & Grow',
      '/daily-checkin': 'Daily Check-In',
      '/daily-check-in': 'Daily Check-In',
      '/crisis': 'Crisis Support',
      '/courses': 'Courses',
      '/course-library': 'Course Library',
      '/meditations': 'Meditations',
      '/meditation-library': 'Meditation Library',
      '/sleep-and-dreams': 'Sleep & Dreams',
      '/telempathy-tools': 'Telempathy Tools',
      '/whoop': 'WHOOP',
      '/whoop-dashboard': 'WHOOP Dashboard',
      '/subscription': 'Subscription',
      '/credits': 'Credits',
      '/settings': 'Settings',
      '/sanctuary': 'Sanctuary',
      '/admin': 'Admin Portal',
      '/school-admin': 'School Admin',
      '/nutrition': 'Food-Mood Intelligence',
      '/inflammation': 'Inflammation Hub',
      '/longevity': 'Longevity',
      '/insights': 'Insights',
      '/breathwork': 'Breathwork',
      '/all-tools': 'All Tools',
      '/vault': 'Vault',
      '/mental-wellness': 'Mental Wellness',
      '/my-scores': 'My Scores',
      '/achievements': 'Achievements',
      '/thought-tracker': 'Thought Tracker',
      '/journal': 'Journal',
      '/exercise': 'Exercise Log',
      '/goals': 'Goals & Habits',
      '/lunar-cycle': 'Lunar & Cycle Tracking',
      '/appointments': 'Appointments',
    };
    
    return routeNames[previousPage] || 'Back';
  };

  return {
    goBack,
    getBackButtonLabel,
    getPreviousPage,
  };
};

